import type { HTMLAttributes } from 'react';
import styled from 'styled-components';

import { BasicParagraph } from '../../../foundations/typefaces/BasicParagraph';
import style from '../../../style';
import type { Size as ThemeSize } from '../../../style/types/theme';
import { Size } from '../constants';
import { Variant } from './constants';
import { TextOption } from './types';

export const Wrapper = styled.div({});

interface BackgroundProps {
  $isActive?: boolean;
}

export const Background = styled.div<BackgroundProps>(
  ({ $isActive = false, theme: { color, size } }) => ({
    borderRadius: size.units(5.5),
    border: `1px solid ${$isActive ? color.border.accent.default() : color.border.primary.default()}`,
    backgroundColor: color.background.primary.default(),
  }),
);

export const StyledIcon = styled.i(({ theme: { size } }) => ({
  height: size.units(2),
  width: size.units(2),
}));

interface ContentWrapperProps {
  $ellipsis: boolean;
  $isActive: boolean;
  $size: Size;
}

export const ContentWrapper = styled.div<ContentWrapperProps>(
  ({ $ellipsis, $isActive, $size, theme: { color, size: themeSize } }) => ({
    display: 'flex',
    position: 'relative',
    padding: `0 ${$size === Size.BIG ? themeSize.units(1.5) : themeSize.units(1)}`,
    alignItems: 'center',
    maxWidth: $ellipsis ? '100%' : undefined,

    [`${StyledIcon}, ${BasicParagraph}`]: {
      color: $isActive
        ? color.content.secondary.active()
        : color.content.secondary.default(),
    },
  }),
);

interface IconWrapperProps {
  $isEndIcon?: boolean;
}

export const IconWrapper = styled.div<IconWrapperProps>(
  ({ $isEndIcon = false, theme: { size } }) => ({
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',

    [StyledIcon]: {
      margin: $isEndIcon ? `0 0 0 ${size.units(1)}` : `0 ${size.units(1)} 0 0`,
    },
  }),
);

interface ChildrenWrapperProps extends HTMLAttributes<HTMLElement> {
  $textOption: TextOption;
  $ellipsis: boolean;
}

const shouldHideText = (textOption: TextOption) =>
  textOption === TextOption.EXPANSIBLE || textOption === TextOption.TOOLTIP;

export const ChildrenWrapper = styled.div<ChildrenWrapperProps>(
  ({ $textOption, $ellipsis }) => ({
    visibility: shouldHideText($textOption) ? 'hidden' : 'visible',
    whiteSpace: 'nowrap',
    opacity: shouldHideText($textOption) ? 0 : 1,
    transition: '0.5s',

    ...($ellipsis && {
      ...style.mixins.ellipsis(true),
    }),

    [BasicParagraph]: {
      ...($ellipsis && {
        ...style.mixins.ellipsis(true),
      }),
    },

    '&::first-letter': {
      textTransform: 'uppercase',
    },
  }),
);

interface ButtonWrapperProps extends HTMLAttributes<HTMLElement> {
  $expandedWidth: string | undefined;
  $isActive?: boolean;
  $textOption: TextOption;
  $variant: Variant;
  $width?: string;
  $size: Size;
}

const getHiddenTextWidth = (size: Size, themeSize: ThemeSize) => {
  return size === Size.BIG ? themeSize.units(5) : themeSize.units(4);
};

const getWidth = (
  textOption: TextOption,
  variant: Variant,
  width: string | undefined,
  size: Size,
  themeSize: ThemeSize,
) => {
  if (variant === Variant.DROPDOWN) {
    return width ?? 'fit-content';
  }

  return shouldHideText(textOption) ? getHiddenTextWidth(size, themeSize) : 'fit-content';
};

const getHoverWidth = (
  textOption: TextOption,
  expandedWidth: string | undefined,
  variant: Variant,
  width: string | undefined,
  size: Size,
  themeSize: ThemeSize,
) => {
  if (variant === Variant.DROPDOWN) {
    return width ?? 'fit-content;';
  }

  if (textOption === TextOption.TOOLTIP) {
    return getHiddenTextWidth(size, themeSize);
  }

  return textOption === TextOption.EXPANSIBLE ? expandedWidth : 'fit-content';
};

const getHeight = ($size: Size, themeSize: ThemeSize) => {
  if ($size === Size.BIG) {
    return themeSize.units(5);
  }

  return themeSize.units(4);
};

export const ButtonWrapper = styled.button<ButtonWrapperProps>(
  ({
    $expandedWidth,
    $isActive = false,
    $textOption = TextOption.STATIC,
    $variant,
    $width,
    $size,
    theme: { color, size: themeSize },
  }) => {
    return {
      width: getWidth($textOption, $variant, $width, $size, themeSize),
      overflow: 'hidden',
      transition: '0.5s',
      position: 'relative',
      height: getHeight($size, themeSize),
      minHeight: getHeight($size, themeSize),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      maxWidth: $width ? '100%' : undefined,
      outline: 'none',

      ...style.mixins.increaseSpecificity({
        borderRadius: themeSize.units(5.5),
      }),

      [Background]: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },

      '&:hover': {
        width: getHoverWidth(
          $textOption,
          $expandedWidth,
          $variant,
          $width,
          $size,
          themeSize,
        ),

        [`${StyledIcon}, ${BasicParagraph}`]: {
          color: color.content.secondary.hover(),
        },
        [ChildrenWrapper]: {
          visibility: $textOption === TextOption.TOOLTIP ? 'hidden' : 'visible',
          transitionDelay: '0.2s',
          opacity: 1,
        },
        [Background]: {
          borderColor: $isActive
            ? color.border.accent.default()
            : color.border.primary.hover(),
        },
      },
    };
  },
);
